import { quickProfanityCheck } from "@/utils/profanityWordListClient";
import { useCallback, useEffect, useState } from "react";

interface UseProfanityCheckOptions {
    /** 디바운스 지연 시간 (ms) */
    debounceMs?: number;
    /** 최소 체크 문자 수 */
    minLength?: number;
}

interface UseProfanityCheckReturn {
    /** 비속어 포함 여부 */
    hasProfanity: boolean;
    /** 체크 중 여부 */
    isChecking: boolean;
    /** 수동 체크 함수 */
    checkText: (text: string) => boolean;
    /** 상태 초기화 */
    reset: () => void;
}

/**
 * 클라이언트용 비속어 체크 훅
 * - 입력 시 즉각적인 피드백 제공
 * - 디바운스로 성능 최적화
 * - 서버에서 전체 필터링 수행되므로 경고 목적
 *
 * @example
 * ```tsx
 * const { hasProfanity } = useProfanityCheck(commentText);
 * {hasProfanity && <Warning>부적절한 표현이 포함되어 있습니다</Warning>}
 * ```
 */
export function useProfanityCheck(
    text: string,
    options: UseProfanityCheckOptions = {}
): UseProfanityCheckReturn {
    const { debounceMs = 150, minLength = 2 } = options;

    const [hasProfanity, setHasProfanity] = useState(false);
    const [isChecking, setIsChecking] = useState(false);

    // 디바운스된 체크
    useEffect(() => {
        if (!text || text.length < minLength) {
            setHasProfanity(false);
            return;
        }

        setIsChecking(true);

        const timer = setTimeout(() => {
            const result = quickProfanityCheck(text);
            setHasProfanity(result);
            setIsChecking(false);
        }, debounceMs);

        return () => {
            clearTimeout(timer);
            setIsChecking(false);
        };
    }, [text, debounceMs, minLength]);

    // 수동 체크 함수 (즉시 실행)
    const checkText = useCallback(
        (inputText: string): boolean => {
            if (!inputText || inputText.length < minLength) {
                return false;
            }
            return quickProfanityCheck(inputText);
        },
        [minLength]
    );

    // 상태 초기화
    const reset = useCallback(() => {
        setHasProfanity(false);
        setIsChecking(false);
    }, []);

    return {
        hasProfanity,
        isChecking,
        checkText,
        reset
    };
}
